import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/lt';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../services/api';
import RegistrationModal from '../components/RegistrationModal';
import SEO from '../components/SEO';

// Set Lithuanian locale
moment.locale('lt');
const localizer = momentLocalizer(moment);

const Rezervacija = () => {
  const [reservations, setReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReservations();
  }, []);

  // Helper to parse date string to local date (avoiding timezone issues)
  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const fetchReservations = async () => {
    try {
      const response = await api.get('/events');
      const reservationsData = response.data.events || response.data;
      const formattedReservations = reservationsData.map(reservation => ({
        id: reservation.id,
        title: reservation.reservation_type === 'admin_block' ? 'Užimta' : 'Rezervuota',
        start: parseLocalDate(reservation.reservation_date),
        end: parseLocalDate(reservation.reservation_date),
        allDay: true,
        reservation_type: reservation.reservation_type,
        isBooked: true
      }));
      setReservations(formattedReservations);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper to format date as YYYY-MM-DD string (local time)
  const formatDateStr = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSelectSlot = ({ start }) => {
    // Check if the date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (start < today) {
      return;
    }

    // Check if date is already booked (using local date strings)
    const dateStr = formatDateStr(start);
    const isBooked = reservations.some(r => {
      const rDate = formatDateStr(r.start);
      return rDate === dateStr;
    });

    if (isBooked) {
      return;
    }

    setSelectedDate(start);
    setIsModalOpen(true);
  };

  const handleSelectEvent = (event) => {
    // Show that this date is already booked
    alert('Ši data jau užimta. Pasirinkite kitą datą.');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedDate(null);
  };

  const handleBookingSuccess = () => {
    fetchReservations();
    handleCloseModal();
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: '#ef4444',
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontWeight: '500',
        cursor: 'not-allowed'
      }
    };
  };

  const dayPropGetter = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Use local date string comparison
    const dateStr = formatDateStr(date);
    const isBooked = reservations.some(r => {
      const rDate = formatDateStr(r.start);
      return rDate === dateStr;
    });

    const isPast = date < today;

    if (isPast) {
      return {
        style: {
          backgroundColor: '#f3f4f6',
          cursor: 'not-allowed'
        }
      };
    }

    if (isBooked) {
      return {
        style: {
          backgroundColor: '#fef2f2',
          cursor: 'not-allowed'
        }
      };
    }

    return {
      style: {
        backgroundColor: '#f0fdf4',
        cursor: 'pointer'
      }
    };
  };

  const messages = {
    today: 'Šiandien',
    previous: 'Atgal',
    next: 'Pirmyn',
    month: 'Mėnuo',
    week: 'Savaitė',
    day: 'Diena',
    agenda: 'Darbotvarkė',
    date: 'Data',
    time: 'Laikas',
    event: 'Rezervacija',
    noEventsInRange: 'Šiuo laikotarpiu rezervacijų nėra.',
    showMore: (total) => `+ dar ${total}`
  };

  return (
    <div>
      <SEO 
        title="Rezervacija"
        description="Rezervuokite poilsį Sporto Krante - pasirinkite datą ir užsisakykite apgyvendinimą, pirtį ar renginių erdvę. Greita ir paprasta rezervacija."
        keywords="rezervacija, užsakymas, apgyvendinimas, poilsis, sporto krantas, datos"
        canonicalUrl="https://sportokrantas.lt/rezervacija"
      />
      {/* Hero Section */}
      <section className="py-12 border-b-[3px] border-black bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-black mb-4 tracking-wide leading-tight text-center" style={{ fontSize: '29.5px', fontWeight: 700, WebkitTextStroke: '0.5px' }}>
            REZERVACIJA
          </h1>
          <p className="text-black text-center max-w-2xl mx-auto mb-8">
            Pasirinkite norimą datą kalendoriuje ir užsirezervuokite vietą. Žalios spalvos dienos yra laisvos, raudonos - užimtos.
          </p>
        </div>
      </section>

      {/* Calendar Section */}
      <section className="py-12 border-b-[3px] border-black bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Legend */}
          <div className="flex items-center justify-center space-x-8 mb-6">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded bg-green-100 border border-green-300"></div>
              <span className="text-sm text-gray-600">Laisva data</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded bg-red-100 border border-red-300"></div>
              <span className="text-sm text-gray-600">Užimta data</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded bg-gray-200 border border-gray-300"></div>
              <span className="text-sm text-gray-600">Praėjusi data</span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="text-black text-lg">Kraunama...</div>
            </div>
          ) : (
            <div style={{ minHeight: '600px' }}>
              <Calendar
                localizer={localizer}
                events={reservations}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 600 }}
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                eventPropGetter={eventStyleGetter}
                dayPropGetter={dayPropGetter}
                messages={messages}
                views={['month']}
                defaultView="month"
                popup
                selectable
              />
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 border-b-[3px] border-black" style={{ backgroundColor: '#D0AB64' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-black mb-6 tracking-wide" style={{ fontSize: '24px', fontWeight: 700, WebkitTextStroke: '0.3px' }}>
            KAIP REZERVUOTI?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="w-16 h-16 rounded-full border-2 border-black flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-black">1</span>
              </div>
              <h3 className="font-bold text-black mb-2">Pasirinkite datą</h3>
              <p className="text-black text-sm">Spustelėkite ant žalios (laisvos) dienos kalendoriuje</p>
            </div>
            <div>
              <div className="w-16 h-16 rounded-full border-2 border-black flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-black">2</span>
              </div>
              <h3 className="font-bold text-black mb-2">Užpildykite formą</h3>
              <p className="text-black text-sm">Įveskite savo kontaktinius duomenis</p>
            </div>
            <div>
              <div className="w-16 h-16 rounded-full border-2 border-black flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-black">3</span>
              </div>
              <h3 className="font-bold text-black mb-2">Gaukite patvirtinimą</h3>
              <p className="text-black text-sm">Mes susisieksime su jumis el. paštu arba telefonu</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedDate={selectedDate}
        onSuccess={handleBookingSuccess}
        bookedDates={reservations.map(r => r.start)}
      />
    </div>
  );
};

export default Rezervacija;
